import React, { useEffect, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FormDetails from 'components/Form/FormDetails';
import { Button, Modal, notification, Spin } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import 'pages/VisitorRequestForm/ReviewForm/ReviewForm.scss';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import { useDispatch, useSelector } from 'react-redux';
import useGuardingFlow from 'core/guardingRequestForm/useGuardingFlow';
import { AppDispatch, CombinedStates } from 'core/types';
import { deleteGuardingRequest, updateGuardingRequestForm, amendGuardingRequestAction } from 'core/guardingRequestForm/guardingRequestFormApi';
import Checkbox from 'components/Elements/Checkbox';
import { unwrapResult } from '@reduxjs/toolkit';
import { VisitRequestFormResponsePayload } from 'core/guardingRequestForm/types';
import TermsAndConditions from 'pages/VisitorRequestForm/components/TermsAndConditionsModal';
import { onBehalfEmailError } from 'core/utils/onBehalfEmailError';

import { GUARDING_STEPPER_LABELS, GUARDING_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';

const GuardingReviewForm = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const flow = useGuardingFlow();
    const { visitRequest, stepData, isUpdating, isAmending } = useSelector((state: CombinedStates) => state.guardingRequestForm);
    const isRetail = stepData?.isRetail || false;
    const { isFetching } = useSelector((state: CombinedStates) => state.visitRequestReview);
    const authedUserData = useSelector((state: CombinedStates) => state.user.userData);
    const currentUserEmail = authedUserData?.email;
    const [termModalOpen, setTermModalOpen] = useState(false);
    const [isTermAccepted, setIsTermAccepted] = useState(false);
    const [amendModalOpen, setAmendModalOpen] = useState(false);
    const [signedBy, setSignedBy] = useState('');
    const [userEmail, setUserEmail] = useState('');

    const [amendedSections, setAmendedSections] = useState<string[]>([]);
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const amending = navState?.amending;

    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequest.encryptedId : null,
        formBasePath: '/guarding-request/',
        deleteEndpoint: 'guarding-request',
        dispatchDelete: () => {
            if (visitRequest.encryptedId) dispatch(deleteGuardingRequest(visitRequest.encryptedId));
        },
    });

    const handleSubmit = () => {
        if (!amending) {
            // A guest submits through the token endpoint, which also consumes
            // the single-use link; either way the declaration is the payload.
            flow.submitReview({
                agreed_to_terms: isTermAccepted,
                signature: signedBy,
                email: userEmail,
            }).then(unwrapResult).then(() => {
                navigation(flow.guestMode ? '/booking/complete' : '/dashboard', { state: null });
            }).catch(reportUnexpectedRejection);
        } else {
            let error = { message: '', description: '' };
            if (amendedSections.length === 0) {
                error = {
                    message: 'No Amendments Made',
                    description: 'Please select at least one section to amend',
                };
            }

            if (error.message !== '' && error.description !== '') {
                notification.error(error);
                return;
            }

            const sections = amendedSections.map((section) => section);

            dispatch(updateGuardingRequestForm({
                id: String(id),
                step: 4,
                data: {
                    declaration: {
                        agreed_to_terms: isTermAccepted,
                        signature: signedBy,
                        email: userEmail,
                    },
                    amending,
                    forcedStep: 4,
                },
            })).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    dispatch(amendGuardingRequestAction({
                        id: String(id),
                        sections,
                        details: additionalDetails,
                    })).then(unwrapResult).then((amendPayload: { message: string, success: boolean }) => {
                        if (amendPayload.success) {
                            navigation(`/visitor-view-request/${id}`, { state: { fromForm: true } });
                        }
                    }).catch(reportUnexpectedRejection);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleNextClick = () => {
        let error = { message: '', description: '' };
        if (!isTermAccepted) {
            error = {
                message: 'Terms & Conditions not accepted',
                description: 'Please accept the terms & conditions to proceed.',
            };
        }

        // Malformed, or the submitter's own address — same rule the visit
        // request form applies, and the API refuses both regardless.
        const emailError = onBehalfEmailError(userEmail, currentUserEmail);
        if (emailError) {
            error = emailError;
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        if (amending) {
            setAmendModalOpen(true);
        } else {
            handleSubmit();
        }
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            const resultAction = await flow.loadStep(4, amending);

            // Redirect to dashboard if guarding request not found
            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Guarding Request Not Found',
                    description: 'The guarding request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Guarding Request',
                description: 'Please select a guarding request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, [id, amending]);

    useEffect(() => {
        if (visitRequest.requester?.name) {
            setSignedBy(visitRequest.requester.name);
        } else if (flow.guestMode && visitRequest.guestRequesterName) {
            // A guest booking has no requester account: the person completing
            // it is the guest the link was issued to, and without this the
            // signature goes up empty and the review step refuses the
            // declaration.
            setSignedBy(visitRequest.guestRequesterName);
        } else if (visitRequest.declaration?.signature) {
            // Fallback (e.g. a resubmitted draft): the previously-captured
            // signature is copied onto the new request, so use it when the
            // requester relation isn't present on the payload.
            setSignedBy(visitRequest.declaration.signature);
        } else if (!flow.guestMode && authedUserData?.name) {
            // Last resort for the authenticated flow: the person completing the
            // request is the logged-in user, so sign with their own name.
            setSignedBy(authedUserData.name);
        }
        // Prefill the "on behalf of" email when amending. Never for a guest —
        // the guest IS the requester, not delegating, so it must stay empty.
        if (amending && visitRequest.onBehalfEmail) {
            setUserEmail(visitRequest.onBehalfEmail);
        }
    }, [visitRequest, amending, flow.guestMode, authedUserData]);

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        // Retail: previous step is Job Details (/step/1); non-retail: Add Visitor (/step/3).
        flow.goToStep(isRetail ? 1 : 3, navigationState);
    };

    const handleRemoveDate = (dateId: number) => {
        if (id !== undefined || flow.guestMode) {
            flow.updateStep(2, {
                selectedDates: visitRequest.dates?.filter((date) => date.id !== dateId),
                amending,
                forcedStep: 2,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    handleInitialLoad();
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleCheckboxChange = (section: string) => {
        if (amendedSections.includes(section)) {
            setAmendedSections((prev) => prev.filter((s) => s !== section));
        } else {
            setAmendedSections((prev) => [...prev, section]);
        }
    };

    return (
        <div>
            <RequestFormLayout
                currentStep={4}
                isNextEnabled={isTermAccepted}
                isBackEnabled
                onBackClick={handleBackClick}
                nextText="Submit"
                onNextClick={handleNextClick}
                stepperLabels={isRetail ? GUARDING_STEPPER_LABELS_RETAIL : GUARDING_STEPPER_LABELS}
                formTitle="Guarding Request Form"
                reduxSliceKey="guardingRequestForm">
                <Spin spinning={isUpdating || isFetching}>
                    <FormDetails
                        onPressTerm={() => setTermModalOpen(true)}
                        details={visitRequest}
                        id={id}
                        signedBy={signedBy}
                        onAcceptTerm={(accept) => setIsTermAccepted(accept)}
                        onRemoveDate={(dateId) => handleRemoveDate(dateId)}
                        amending={amending}
                        onEmailChange={(email) => setUserEmail(email)}
                        userEmail={userEmail} />
                </Spin>
            </RequestFormLayout>
            <Modal title="Terms & Conditions For Access"
                wrapClassName="modal-container"
                onCancel={() => setTermModalOpen(false)}
                footer={null}
                styles={{ body: { maxHeight: '50vh', overflow: 'auto' } }}
                style={{ left: -200 }}
                open={termModalOpen}>
                <TermsAndConditions />
            </Modal>
            <Modal title="Amendments to Request"
                wrapClassName="amend-modal-container"
                onCancel={() => setAmendModalOpen(false)}
                footer={null}
                styles={{ body: { overflow: 'auto' } }}
                open={amendModalOpen}>
                <div>
                    <p>
                        Please select all the sections of your request that you made amendments to.
                        Depending on changes made, the request will return to the pending approval
                        status until an approver reviews your amendments.
                    </p>
                    <p className="amend-important-notice">
                        IMPORTANT - If making changes to Date or Shift please ensure the check box is selected so any resource allocated for granting access can be notified.
                    </p>
                    <div className="amend-checkbox-wrapper">
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Job Details')} onClick={() => handleCheckboxChange('Job Details')} />
                            <span className="checkbox-label">Job Details</span>
                        </div>
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Date or Shift')} onClick={() => handleCheckboxChange('Date or Shift')} />
                            <span className="checkbox-label">Date or Shift</span>
                        </div>
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Visitors')} onClick={() => handleCheckboxChange('Visitors')} />
                            <span className="checkbox-label">Visitors</span>
                        </div>
                    </div>
                    <div className="additional-details">
                        <h5 className="heading margin-5">Additional Details (Optional)</h5>
                        <textarea
                            className="details-textarea"
                            value={additionalDetails}
                            maxLength={MAX_LONG_TEXT_LENGTH}
                            placeholder=""
                            onChange={(e) => setAdditionalDetails(e.target.value)} />
                    </div>
                    <div className="button-wrapper">
                        <Button type="primary" className="form-button" onClick={handleSubmit} loading={isAmending || isUpdating}>
                            Save Amendments
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>

    );
};

export default GuardingReviewForm;
